import { create } from 'zustand'
import type { ConstraintMap } from './utils/resellerConstraints'
import type {
  BlueprintConfig,
  BlueprintCountryRuleset,
  BlueprintCurrentVendor,
  BlueprintDataInput,
  BlueprintFuzziness,
  BlueprintMaturityDimension,
  BlueprintPriorityGap,
  BlueprintRecommendedAction,
  BlueprintSignalEntry,
  BlueprintStakeholder,
  BlueprintPostCallUpdate,
  BlueprintVerificationLayer,
  BuilderWorkflow,
  ComplexWorkflow,
  AmlConfig,
  BiometricsConfig,
  FeatureRequest,
  GeneralInfo,
  KycConfig,
  KybConfig,
  Recipe,
  RiskBranch,
  RiskBranchOutcome,
  RiskConfig,
  CustomRiskFactor,
  RulesConfig,
  ScopeData,
  ScopeHour,
  CreditHeaderCheckMode,
  Workflow,
  WorkflowStep,
  WorkflowStepType,
  SowConfig,
  SowMilestone,
  SowPhase,
  SowActivity,
  GanttTask,
  RaciColumn,
  RaciEntry,
  RaciValue,
} from './types'
import { V1_RECIPES } from './types'

// Re-export shared utilities so existing consumers (components, tests) don't break
export {
  getDefaultGeneralInfo,
  getDefaultKycConfig,
  getDefaultAmlConfig,
  getDefaultBiometricsConfig,
  getDefaultKybConfig,
  getDefaultRecipe,
  getDefaultRulesConfig,
  getDefaultRiskConfig,
  getDefaultSowConfig,
  getDefaultScopeData,
} from './utils/defaults'

export { normalizeTriState, isYes, isUnknown, hasAnyNonUnknown, calculateTabCompletion } from './utils/completion'

import {
  getDefaultGeneralInfo,
  getDefaultKycConfig,
  getDefaultAmlConfig,
  getDefaultBiometricsConfig,
  getDefaultKybConfig,
  getDefaultRecipe,
  getDefaultRulesConfig,
  getDefaultRiskConfig,
  getDefaultScopeData,
  getDefaultRaciTemplate,
  getDefaultGanttTemplate,
  getDefaultDataInputs,
  DEFAULT_PREP_CHECKLIST,
} from './utils/defaults'

import { normalizeTriState, calculateTabCompletion } from './utils/completion'
import { getSectionForField, markSectionCustomized, unmarkSectionCustomized, SOW_SECTION_FIELDS } from './utils/sowSections'
import type { SowSectionKey } from './utils/sowSections'

// The following normalizer functions remain here as they are only used within the store.

// Old defaults and completion functions have been extracted to ./utils/defaults.ts and ./utils/completion.ts

const normalizeCreditHeaderCheckMode = (value: unknown): CreditHeaderCheckMode => {
  if (value === 'on_behalf') return 'yes'
  return normalizeTriState(value)
}

const normalizeRiskConfig = (raw: Partial<RiskConfig> | undefined): RiskConfig => {
  const defaults = getDefaultRiskConfig()

  const merged = {
    ...defaults,
    ...(raw || {}),
  } as RiskConfig

  // Legacy migration: previous schema stored custom factors as { key, values: [{ matchValue, pointsAllocated, ... }] }
  const legacyCustomFactors = (raw as any)?.customFactors
  const hasLegacyShape =
    Array.isArray(legacyCustomFactors) &&
    legacyCustomFactors.some((f: any) => f && typeof f === 'object' && 'key' in f && Array.isArray(f.values))

  if (hasLegacyShape) {
    const migrated: CustomRiskFactor[] = legacyCustomFactors.map((f: any, idx: number) => {
      const name = typeof f.key === 'string' ? f.key : `Custom factor ${idx + 1}`
      const values = Array.isArray(f.values) ? f.values : []

      return {
        id: `custom_factor_${Date.now()}_${idx}_${Math.random().toString(16).slice(2)}`,
        name,
        description: '',
        scoringMethod: 'lookup',
        kvpType: 'general.string',
        includeZeroScores: false,
        defaultValue: '',
        defaultScore: 0,
        appliesTo: 'both',
        kvpKeyOverride: '',
        portalVisible: false,
        lookupMappings: values
          .filter((v: any) => v && typeof v === 'object')
          .map((v: any) => ({
            value: typeof v.matchValue === 'string' ? v.matchValue : '',
            score: typeof v.pointsAllocated === 'number' ? v.pointsAllocated : Number(v.pointsAllocated) || 0,
          }))
          .filter((m: any) => typeof m.value === 'string'),
        rangeMappings: [],
        truthyScore: 0,
        falsyScore: 0,
      }
    })

    return {
      ...merged,
      customFactors: migrated,
    }
  }

  return merged
}

const normalizeKycConfig = (raw: Partial<KycConfig> | undefined): KycConfig => {
  const defaults = getDefaultKycConfig()
  const merged = {
    ...defaults,
    ...(raw || {}),
  } as KycConfig

  return {
    ...merged,
    isReportingEntity: normalizeTriState(merged.isReportingEntity),
    enableConsentSources: normalizeCreditHeaderCheckMode(merged.enableConsentSources),
    acceptNzCustomers: normalizeTriState(merged.acceptNzCustomers),

    auOtherDocumentTypesEnabled: Boolean((merged as any).auOtherDocumentTypesEnabled),
    auOtherDocumentTypesText: typeof (merged as any).auOtherDocumentTypesText === 'string' ? (merged as any).auOtherDocumentTypesText : '',

    internationalDocumentAcceptance:
      (merged as any).internationalDocumentAcceptance && typeof (merged as any).internationalDocumentAcceptance === 'object'
        ? (merged as any).internationalDocumentAcceptance
        : {},

    requiredDataElements: {
      ...defaults.requiredDataElements,
      ...(merged.requiredDataElements || {}),
    },

    primaryCustomerJurisdictions:
      merged.primaryCustomerJurisdictions && merged.primaryCustomerJurisdictions.length > 0
        ? merged.primaryCustomerJurisdictions
        : defaults.primaryCustomerJurisdictions,
    internationalCountries:
      merged.internationalCountries && merged.internationalCountries.length > 0
        ? merged.internationalCountries
        : defaults.internationalCountries,

    auGovernmentSources: {
      ...defaults.auGovernmentSources,
      ...(merged.auGovernmentSources || {}),
    },
    creditBureauSources: {
      ...defaults.creditBureauSources,
      ...(merged.creditBureauSources || {}),
    },
    alternativeAuSources: {
      ...defaults.alternativeAuSources,
      ...(merged.alternativeAuSources || {}),
    },
    internationalDataSources: {
      ...defaults.internationalDataSources,
      ...(merged.internationalDataSources || {}),
    },

    manualReviewTriggers: {
      ...defaults.manualReviewTriggers,
      ...(merged.manualReviewTriggers || {}),
    },
  }
}

const normalizeAmlConfig = (raw: Partial<AmlConfig> | undefined): AmlConfig => {
  const defaults = getDefaultAmlConfig()
  const r = (raw || {}) as any

  // Data migration: reverse mapAmlStoreToNewSchema keys back to AmlConfig shape
  // (for data saved with the old mapper that used different key names)
  // After migrating, delete old keys so they don't persist and confuse isPersistedAmlConfig on next load.
  if (r.amlCheckLevel && !r.level) r.level = r.amlCheckLevel
  delete r.amlCheckLevel
  if (r.amlRelationship && !r.relationship) r.relationship = r.amlRelationship
  delete r.amlRelationship
  if (r.amlVendor && !r.vendor) r.vendor = r.amlVendor
  delete r.amlVendor
  if (r.otherAmlVendor && !r.otherVendor) r.otherVendor = r.otherAmlVendor
  delete r.otherAmlVendor
  if (r.pepScreeningLevels && !r.pepLevels) r.pepLevels = r.pepScreeningLevels
  delete r.pepScreeningLevels
  if (r.nameMatchingFuzziness && !r.fuzziness) r.fuzziness = r.nameMatchingFuzziness
  delete r.nameMatchingFuzziness
  if (r.dataStorageProcessing) {
    if (r.dataStorageProcessing.storeInAustralia !== undefined && r.storeInAustralia === undefined) r.storeInAustralia = r.dataStorageProcessing.storeInAustralia
    if (r.dataStorageProcessing.sendCountriesForPep !== undefined && r.sendCountriesForPep === undefined) r.sendCountriesForPep = r.dataStorageProcessing.sendCountriesForPep
    delete r.dataStorageProcessing
  }
  if (r.customSanctionsLists && !r.customBlockList) r.customBlockList = r.customSanctionsLists
  delete r.customSanctionsLists

  const merged = {
    ...defaults,
    ...r,
  } as any

  // Deep-merge nested objects so that saved data missing newer keys still gets defaults.
  // Without this, `{ ...defaults, ...r }` replaces the ENTIRE sub-object with the incomplete saved one.
  const nestedAmlKeys: Array<keyof AmlConfig> = [
    'screeningEntityTypes',
    'pepScreeningConfig',
    'sanctionsScreeningConfig',
    'adverseMediaScreeningConfig',
    'additionalWatchlistsConfig',
    'batchProcessing',
    'jurisdictionScreening',
    'nameMatchingStrategy',
    'volumeEstimates',
  ]
  for (const key of nestedAmlKeys) {
    if (defaults[key] && typeof defaults[key] === 'object' && !Array.isArray(defaults[key])) {
      const saved = r[key] && typeof r[key] === 'object' && !Array.isArray(r[key]) ? r[key] : {}
      merged[key] = { ...defaults[key], ...saved }
    }
  }

  // Data migration: merge customSanctions + customWatchlists → customBlockList
  if (!merged.customBlockList && (merged.customSanctions || merged.customWatchlists)) {
    const parts: string[] = []
    if (merged.customSanctions) parts.push(`Sanctions:\n${merged.customSanctions}`)
    if (merged.customWatchlists) parts.push(`Watchlists:\n${merged.customWatchlists}`)
    merged.customBlockList = parts.join('\n\n')
  }

  // Data migration: rename sources → category in adverseMediaScreeningConfig
  if (merged.adverseMediaScreeningConfig) {
    if (merged.adverseMediaScreeningConfig.sources && !merged.adverseMediaScreeningConfig.category) {
      merged.adverseMediaScreeningConfig.category = merged.adverseMediaScreeningConfig.sources
    }
    // Ensure customCategories field exists
    if (!merged.adverseMediaScreeningConfig.customCategories) {
      merged.adverseMediaScreeningConfig.customCategories = ''
    }
  }

  // Ensure sanctionsScreeningConfig has customLists field
  if (merged.sanctionsScreeningConfig && !merged.sanctionsScreeningConfig.customLists) {
    merged.sanctionsScreeningConfig.customLists = ''
  }

  // Ensure new fields exist with defaults
  if (!merged.batchProcessing) {
    merged.batchProcessing = {
      required: 'unknown',
      details: '',
    }
  }

  if (!merged.jurisdictionScreening) {
    merged.jurisdictionScreening = {
      screenAllJurisdictions: 'unknown',
      specificJurisdictions: '',
    }
  }

  // Normalize monitoring type to include 'unknown' as default
  if (!merged.monitoringType || merged.monitoringType === '') {
    merged.monitoringType = 'unknown'
  }

  return merged as AmlConfig
}

const normalizeBiometricsConfig = (raw: Partial<BiometricsConfig> | undefined): BiometricsConfig => {
  const defaults = getDefaultBiometricsConfig()
  const merged = {
    ...defaults,
    ...(raw || {}),
  } as BiometricsConfig

  const flowsInput = Array.isArray((merged as any).flows) ? ((merged as any).flows as BiometricsConfig['flows']) : []
  const hasFlows = flowsInput.length > 0

  const legacyVendor = typeof (merged as any).vendor === 'string' ? (merged as any).vendor : ''
  const legacyServiceLevel = typeof (merged as any).serviceLevel === 'string' ? (merged as any).serviceLevel : ''

  const inferredFlow: BiometricsConfig['flows'][number] = {
    id: 'flow_1',
    name: 'Flow 1',
    notes: '',
    isDefault: true,
    idVerificationEnabled: 'yes',
    idFormEnabled: 'unknown',
    biometricsEnabled: legacyServiceLevel === 'document_biometrics_review' ? 'yes' : 'no',
    reviewScreenEnabled:
      legacyServiceLevel === 'document_review' || legacyServiceLevel === 'document_biometrics_review' ? 'yes' : 'no',
    consentScreenEnabled: normalizeTriState((merged as any).customConsents),
    redirectPageEnabled: normalizeTriState((merged as any).customRedirectPage),
    customScreenEnabled: 'unknown',
    customScreenDetails: '',
    doubleIdEnabled: 'unknown' as any,
    vendor: legacyVendor === 'daon' ? 'daon' : legacyVendor === 'incode' ? 'incode' : 'unknown',
    documentTypes: [],
    customDocumentTypeDetails: '',
  }

  const flowsRaw = hasFlows ? flowsInput.slice(0, 5) : [inferredFlow]
  const anyDefault = flowsRaw.some((f) => Boolean((f as any).isDefault))
  const normalizedFlows = flowsRaw.map((f, i) => {
    const isDefault = anyDefault ? Boolean((f as any).isDefault) : i === 0
    return {
      ...f,
      isDefault,
      consentScreenEnabled: normalizeTriState((f as any).consentScreenEnabled),
      idFormEnabled: normalizeTriState((f as any).idFormEnabled),
      idVerificationEnabled: normalizeTriState((f as any).idVerificationEnabled),
      biometricsEnabled: normalizeTriState((f as any).biometricsEnabled),
      reviewScreenEnabled: normalizeTriState((f as any).reviewScreenEnabled),
      redirectPageEnabled: normalizeTriState((f as any).redirectPageEnabled),
      customScreenEnabled: normalizeTriState((f as any).customScreenEnabled),
      customScreenDetails: typeof (f as any).customScreenDetails === 'string' ? (f as any).customScreenDetails : '',
      doubleIdEnabled: normalizeTriState((f as any).doubleIdEnabled),
      vendor: (f as any).vendor || 'unknown',
      documentTypes: Array.isArray((f as any).documentTypes) ? (f as any).documentTypes : [],
      customDocumentTypeDetails: typeof (f as any).customDocumentTypeDetails === 'string' ? (f as any).customDocumentTypeDetails : '',
    }
  })
  const firstDefaultIndex = normalizedFlows.findIndex((f) => f.isDefault)
  const singleDefaultFlows = normalizedFlows.map((f, i) => ({ ...f, isDefault: i === firstDefaultIndex }))

  // Apply default SMS template to existing scopes with empty templates
  const defaultSmsTemplate = `Header: Verify ID
Message: Hi {{customer_name}}, You're almost there. We just need you to head to {{onboarding_url}} within the next {{link_validity_hours}} hours to complete the verification process.`
  const existingSmsTemplate = (merged as any).customSmsMessageTemplate
  const smsTemplate = typeof existingSmsTemplate === 'string' && existingSmsTemplate.trim() !== ''
    ? existingSmsTemplate
    : defaultSmsTemplate

  return {
    ...merged,
    enabled: normalizeTriState(merged.enabled),
    serviceLevel: (merged.serviceLevel || '') as BiometricsConfig['serviceLevel'],
    flows: singleDefaultFlows,
    smsSettingsConfirmed: normalizeTriState((merged as any).smsSettingsConfirmed),
    customSmsNotRequired: (merged as any).customSmsNotRequired === true,
    customSmsMessageTemplate: smsTemplate,
    smsLinkValidityHours:
      typeof (merged as any).smsLinkValidityHours === 'number'
        ? (merged as any).smsLinkValidityHours
        : (merged as any).smsLinkValidityHours === null
          ? null
          : null,
    frontend: ((merged.frontend as any) || 'unknown') as BiometricsConfig['frontend'],
    platform: ((merged.platform as any) || 'unknown') as BiometricsConfig['platform'],
    rebrandingHostedOneSDK: normalizeTriState(merged.rebrandingHostedOneSDK),
    rebrandingDaon: normalizeTriState(merged.rebrandingDaon),
    extendedBranding: normalizeTriState(merged.extendedBranding),
    customRedirectPage: normalizeTriState(merged.customRedirectPage),
    customConsents: normalizeTriState(merged.customConsents),
    documentTypes: Array.isArray(merged.documentTypes) ? merged.documentTypes : [],
  }
}

function normalizeKybConfig(raw: any): KybConfig {
  const defaults = getDefaultKybConfig()
  const merged = { ...defaults, ...raw }

  // Migrate old boolean 'enabled' to TriState
  if (typeof raw?.enabled === 'boolean') {
    merged.enabled = raw.enabled ? 'yes' : 'no'
  }

  // Migrate old auChecks fields
  if (raw?.auChecks) {
    merged.auChecks = {
      amlStandard: raw.auChecks.amlStandard || false,
      amlEnhanced: raw.auChecks.amlEnhanced || false,
      asicInfo: raw.auChecks.asicInfo || false,
      ubo: raw.auChecks.ubo || false,
      creditReport: raw.auChecks.creditReport || raw.auChecks.creditScore || false,
    }
  }

  // Migrate old roleChecksEnabled → kycForIndividualsRequired
  if (raw?.roleChecksEnabled && !raw?.kycForIndividualsRequired) {
    merged.kycForIndividualsRequired = raw.roleChecksEnabled === 'yes' ? 'yes' : raw.roleChecksEnabled === 'no' ? 'no' : 'unknown'
  }

  // Migrate old amlScreeningEnabled → amlEntityScreening
  if (raw?.amlScreeningEnabled && !raw?.amlEntityScreening) {
    merged.amlEntityScreening = raw.amlScreeningEnabled
  }

  // Migrate old entityTypes array → entityTypeSplit percentages
  if (Array.isArray(raw?.entityTypes) && raw.entityTypes.length > 0 && !raw?.entityTypeSplit) {
    const count = raw.entityTypes.length
    const share = Math.round(100 / count)
    const typeMap: Record<string, keyof typeof defaults.entityTypeSplit> = {
      sole_trader: 'soleTradersPercent', pty_ltd: 'ptyLtdPercent', company: 'companyPercent',
      partnership: 'partnershipPercent', trust: 'trustPercent', other: 'otherPercent',
    }
    merged.entityTypeSplit = { ...defaults.entityTypeSplit }
    for (const t of raw.entityTypes) {
      const key = typeMap[t]
      if (key) merged.entityTypeSplit[key] = share
    }
  }

  // Migrate old internationalCountries + internationalChecks → internationalCountryProducts
  if (Array.isArray(raw?.internationalCountries) && raw.internationalCountries.length > 0 && !raw?.internationalCountryProducts) {
    const oldChecks = Array.isArray(raw.internationalChecks) ? raw.internationalChecks : []
    // Map old check IDs to new product IDs
    const productMap: Record<string, string> = { company_profile: 'data_retrieval', registry_documents: 'registry_documents', aml_check: 'data_retrieval' }
    const products = [...new Set(oldChecks.map((c: string) => productMap[c] || c).filter(Boolean))]
    merged.internationalCountryProducts = raw.internationalCountries.map((country: string) => ({ country, products: [...products] }))
  }

  // Migrate old trustTypes array → trustTypeSplit percentages
  if (Array.isArray(raw?.trustTypes) && raw.trustTypes.length > 0 && !raw?.trustTypeSplit) {
    const count = raw.trustTypes.length
    const share = Math.round(100 / count)
    const typeMap: Record<string, keyof typeof defaults.trustTypeSplit> = {
      unit: 'unitPercent', discretionary: 'discretionaryPercent', smsf: 'smsfPercent', other: 'otherPercent',
    }
    merged.trustTypeSplit = { ...defaults.trustTypeSplit }
    for (const t of raw.trustTypes) {
      const key = typeMap[t]
      if (key) merged.trustTypeSplit[key] = share
    }
  }

  // Set defaults for section-level enabled fields
  merged.auChecksEnabled = merged.auChecksEnabled || 'unknown'
  merged.kycForIndividualsRequired = merged.kycForIndividualsRequired || 'unknown'
  merged.uboConsentAware = merged.uboConsentAware || 'unknown'
  merged.uboSmsPhoneAware = merged.uboSmsPhoneAware || 'unknown'
  merged.uboWorkflowId = merged.uboWorkflowId || ''
  merged.amlEntityScreening = merged.amlEntityScreening || 'unknown'
  merged.amlEntityScreeningTypes = merged.amlEntityScreeningTypes || []
  merged.entityTypesEnabled = merged.entityTypesEnabled || 'unknown'
  merged.entityTypeSplit = merged.entityTypeSplit || defaults.entityTypeSplit
  merged.internationalEnabled = merged.internationalEnabled || 'unknown'
  merged.internationalCountryProducts = merged.internationalCountryProducts || []
  merged.trustAnalyserEnabled = merged.trustAnalyserEnabled || 'unknown'
  merged.trustTypeSplit = merged.trustTypeSplit || defaults.trustTypeSplit
  merged.onboardingFormEnabled = merged.onboardingFormEnabled || 'unknown'
  merged.onboardingFormBranded = merged.onboardingFormBranded || 'unknown'
  merged.agingEnabled = merged.agingEnabled ?? true
  merged.agingPeriodMonths = merged.agingPeriodMonths ?? 12
  merged.agingCustomConfig = merged.agingCustomConfig ?? ''

  // Remove legacy fields that no longer exist on the type
  delete merged.roleChecksEnabled
  delete merged.roleChecks
  delete merged.amlScreeningEnabled
  delete merged.amlScreeningEntityTypes
  delete merged.entityTypes
  delete merged.internationalCountries
  delete merged.internationalChecksEnabled
  delete merged.internationalChecks
  delete merged.trustTypes

  return merged as KybConfig
}

interface ScopeStore {
  scope: ScopeData
  tabCompletions: Record<string, number>

  // Actions
  setScope: (scope: ScopeData) => void
  resetScope: () => void
  updateCustomerName: (name: string) => void
  updateCustomerAbn: (abn: string) => void
  updateHubspotDeal: (dealId: string | null, dealName: string | null) => void

  // General Info
  updateGeneralInfo: <K extends keyof GeneralInfo>(field: K, value: GeneralInfo[K]) => void

  // KYC Config
  updateKycConfig: <K extends keyof KycConfig>(field: K, value: KycConfig[K]) => void
  toggleKycDocumentAu: (docId: string) => void
  toggleKycDocumentNz: (docId: string) => void
  addKycInternationalCountry: (country: string) => void
  removeKycInternationalCountry: (country: string) => void

  // KYC Ruleset actions
  addKycRuleset: (rulesetKey: string, name?: string) => void
  updateKycRuleset: (id: number, rulesetKey: string, customRequirements?: KycConfig['rulesets'][0]['customRequirements'], name?: string) => void
  removeKycRuleset: (id: number) => void

  // AML Config
  updateAmlConfig: <K extends keyof AmlConfig>(field: K, value: AmlConfig[K]) => void
  togglePepLevel: (level: string) => void

  // Biometrics Config
  updateBiometricsConfig: <K extends keyof BiometricsConfig>(field: K, value: BiometricsConfig[K]) => void
  toggleBiometricsDocumentType: (docId: string) => void
  addBiometricsFlow: () => void
  applyBiometricsFlowTemplate: (index: number, templateId: 'idv_daon' | 'idv_incode') => void
  updateBiometricsFlow: (index: number, updates: Partial<BiometricsConfig['flows'][number]>) => void
  removeBiometricsFlow: (index: number) => void
  setDefaultBiometricsFlow: (index: number) => void

  // KYB Config
  updateKybConfig: <K extends keyof KybConfig>(field: K, value: KybConfig[K]) => void
  toggleKybAuCheck: (checkId: keyof KybConfig['auChecks']) => void
  toggleAmlEntityScreeningType: (type: string) => void
  updateEntityTypeSplit: (field: keyof KybConfig['entityTypeSplit'], value: number) => void
  addKybInternationalCountry: (country: string) => void
  removeKybInternationalCountry: (country: string) => void
  toggleKybInternationalCountryProduct: (country: string, product: string) => void
  updateTrustTypeSplit: (field: keyof KybConfig['trustTypeSplit'], value: number) => void

  // Recipes
  addRecipe: () => void
  updateRecipe: (id: number, updates: Partial<Recipe>) => void
  removeRecipe: (id: number) => void
  toggleRecipeProduct: (recipeId: number, productId: keyof Recipe['products']) => void

  // Workflows
  addWorkflow: () => void
  addPresetWorkflow: (preset: 'full' | 'simple' | 'step_up') => void
  addV1RecipeWorkflow: (recipeKey: string) => void
  addV1CustomWorkflow: () => void
  addComplexWorkflow: () => void
  updateWorkflow: (
    id: string,
    updates: Partial<Omit<BuilderWorkflow, 'id'>> | Partial<Omit<ComplexWorkflow, 'id'>>
  ) => void
  removeWorkflow: (id: string) => void
  addWorkflowStep: (workflowId: string, stepType: WorkflowStepType) => void
  updateWorkflowStep: (workflowId: string, stepId: string, updates: Partial<WorkflowStep>) => void
  removeWorkflowStep: (workflowId: string, stepId: string) => void
  moveWorkflowStep: (workflowId: string, stepId: string, direction: 'up' | 'down') => void
  reorderWorkflowStep: (workflowId: string, stepId: string, targetIndex: number) => void
  updateWorkflowsMeta: (field: 'workflowsNotes' | 'workflowsRequiredCount', value: string | number | null) => void

  // Rules Config
  updateRulesConfig: <K extends keyof RulesConfig>(field: K, value: RulesConfig[K]) => void
  addCustomDuplicateRule: () => void
  updateCustomDuplicateRule: (index: number, updates: Partial<import('./types').DuplicateRule>) => void
  removeCustomDuplicateRule: (index: number) => void
  addCustomBlocklistEntry: () => void
  updateCustomBlocklistEntry: (index: number, updates: Partial<import('./types').BlocklistEntry>) => void
  removeCustomBlocklistEntry: (index: number) => void

  // Risk Config (stored under Rules tab)
  setRiskConfig: (riskConfig: RiskConfig) => void
  updateRiskConfig: <K extends keyof RiskConfig>(field: K, value: RiskConfig[K]) => void

  // Scope Hours
  addScopeHour: (hour: Omit<ScopeHour, 'id'>) => void
  updateScopeHour: (id: number, updates: Partial<ScopeHour>) => void
  removeScopeHour: (id: number) => void

  // Feature Requests
  addFeatureRequest: () => void
  updateFeatureRequest: (id: number, updates: Partial<FeatureRequest>) => void
  removeFeatureRequest: (id: number) => void

  // SOW Config
  updateSowConfig: <K extends keyof SowConfig>(field: K, value: SowConfig[K]) => void
  addSowMilestone: () => void
  updateSowMilestone: (id: number, updates: Partial<SowMilestone>) => void
  removeSowMilestone: (id: number) => void
  addSowPhase: () => void
  updateSowPhase: (id: number, updates: Partial<SowPhase>) => void
  removeSowPhase: (id: number) => void
  setSowPhaseDatesByIndex: (index: number, patch: { startDate?: string; endDate?: string }) => void
  addSowActivity: (phaseId: number) => void
  updateSowActivity: (id: number, updates: Partial<SowActivity>) => void
  removeSowActivity: (id: number) => void
  addGanttTask: (phaseId: number) => void
  updateGanttTask: (id: number, updates: Partial<GanttTask>) => void
  removeGanttTask: (id: number) => void
  reorderGanttTask: (id: number, direction: 'up' | 'down') => void
  // RACI Chart
  addRaciColumn: (name: string) => void
  removeRaciColumn: (columnId: string) => void
  updateRaciColumnName: (columnId: string, name: string) => void
  addRaciEntry: () => void
  updateRaciEntry: (id: number, updates: Partial<RaciEntry>) => void
  updateRaciAssignment: (entryId: number, columnId: string, value: RaciValue) => void
  removeRaciEntry: (id: number) => void
  loadDefaultRaci: () => void
  loadDefaultGantt: () => void

  // SOW Template
  sowTemplate: SowConfig | null
  setSowTemplate: (template: SowConfig) => void
  resetSowSection: (sectionKey: SowSectionKey) => void

  // Finalization
  finalizeScope: (finalizedBy: string) => void
  unfinalizeScope: () => void

  // Blueprint
  updateBlueprintField: <K extends keyof BlueprintConfig>(field: K, value: BlueprintConfig[K]) => void
  addBlueprintVendor: () => void
  updateBlueprintVendor: (id: number, updates: Partial<BlueprintCurrentVendor>) => void
  removeBlueprintVendor: (id: number) => void
  addBlueprintSignal: () => void
  updateBlueprintSignal: (id: number, updates: Partial<BlueprintSignalEntry>) => void
  removeBlueprintSignal: (id: number) => void
  addBlueprintCountryRuleset: () => void
  updateBlueprintCountryRuleset: (id: number, updates: Partial<BlueprintCountryRuleset>) => void
  removeBlueprintCountryRuleset: (id: number) => void
  toggleBlueprintCountryInputData: (id: number, dataType: string) => void
  updateBlueprintCountryInputFuzziness: (id: number, dataType: string, fuzziness: BlueprintFuzziness) => void
  updateBlueprintCountryInputCaptureMethod: (id: number, dataType: string, captureMethod: string) => void
  updateBlueprintCountryInputField: (id: number, dataType: string, updates: Partial<BlueprintDataInput>) => void
  addBlueprintStakeholder: () => void
  updateBlueprintStakeholder: (id: number, updates: Partial<BlueprintStakeholder>) => void
  removeBlueprintStakeholder: (id: number) => void
  addBlueprintPostCallUpdate: () => void
  updateBlueprintPostCallUpdate: (id: number, updates: Partial<BlueprintPostCallUpdate>) => void
  removeBlueprintPostCallUpdate: (id: number) => void
  toggleBlueprintPrepCheck: (checkId: string) => void
  resetBlueprintPrepChecklist: () => void
  updateBlueprintVerificationLayer: (id: string, updates: Partial<BlueprintVerificationLayer>) => void
  addBlueprintVerificationLayer: () => void
  removeBlueprintVerificationLayer: (id: string) => void
  updateBlueprintMaturityDimension: (id: string, updates: Partial<BlueprintMaturityDimension>) => void
  addBlueprintPriorityGap: () => void
  updateBlueprintPriorityGap: (id: number, updates: Partial<BlueprintPriorityGap>) => void
  removeBlueprintPriorityGap: (id: number) => void
  addBlueprintRecommendedAction: (bucket: 'quickWins' | 'foundationMoves' | 'strategicShifts') => void
  updateBlueprintRecommendedAction: (bucket: 'quickWins' | 'foundationMoves' | 'strategicShifts', id: number, text: string) => void
  removeBlueprintRecommendedAction: (bucket: 'quickWins' | 'foundationMoves' | 'strategicShifts', id: number) => void

  // Notes
  updateNotes: (notes: string) => void

  // Reseller constraints
  resellerConstraints: ConstraintMap | null
  parentCustomerName: string | null
  setResellerConstraints: (constraints: ConstraintMap | null, parentName: string | null) => void
}

// Helper: auto-mark a SOW section as customized when a field in that section changes
const autoMarkSowSection = (state: { scope: ScopeData }, sectionKey: SowSectionKey) => ({
  sowCustomizedSections: markSectionCustomized(state.scope.sowCustomizedSections, sectionKey),
})

export const useScopeStore = create<ScopeStore>((set) => ({
  scope: getDefaultScopeData(),
  tabCompletions: calculateTabCompletion(getDefaultScopeData()),
  sowTemplate: null,
  resellerConstraints: null,
  parentCustomerName: null,

  setScope: (scope) =>
    set(() => {
      const normalizeWorkflowSteps = (steps: unknown): WorkflowStep[] => {
        if (!Array.isArray(steps)) return []

        return steps
          .map((s): WorkflowStep | null => {
            if (!s || typeof s !== 'object') return null
            const sr = s as Record<string, unknown>
            const id = typeof sr.id === 'string' ? sr.id : `step_${Date.now()}`
            const type = sr.type

            if (type === 'blocklist') return { id, type }
            if (type === 'gov_id') return { id, type }
            if (type === 'aml') return { id, type }
            if (type === 'fraud') return { id, type }
            if (type === 'death_check') return { id, type }
            if (type === 'duplicate') return { id, type, notes: typeof sr.notes === 'string' ? sr.notes : '' }

            if (type === 'idv_biometrics') {
              const requirement = sr.requirement === 'optional' ? 'optional' : 'mandatory'
              return {
                id,
                type,
                flowId: typeof sr.flowId === 'string' ? sr.flowId : null,
                requirement,
              }
            }

            if (type === 'kyc') {
              return {
                id,
                type,
                rulesetId: typeof sr.rulesetId === 'string' ? sr.rulesetId : '',
                cascadeSources: Array.isArray(sr.cascadeSources)
                  ? (sr.cascadeSources.filter((x) => typeof x === 'string') as any)
                  : [],
              }
            }

            if (type === 'risk') {
              const branchesFromPersisted = Array.isArray(sr.branches) ? sr.branches : undefined
              const rulesFromLegacy = Array.isArray(sr.rules) ? sr.rules : undefined

              const branches: RiskBranch[] = branchesFromPersisted
                ? (branchesFromPersisted
                    .map((b): RiskBranch | null => {
                      if (!b || typeof b !== 'object') return null
                      const br = b as Record<string, unknown>
                      return {
                        id: typeof br.id === 'string' ? br.id : `branch_${Date.now()}`,
                        condition: typeof br.condition === 'string' ? br.condition : '',
                        outcome: typeof br.outcome === 'string' ? (br.outcome as any) : '',
                        steps: normalizeWorkflowSteps(br.steps),
                      }
                    })
                    .filter(Boolean) as RiskBranch[])
                : rulesFromLegacy
                  ? (rulesFromLegacy
                      .map((r): RiskBranch | null => {
                        if (!r || typeof r !== 'object') return null
                        const rr = r as Record<string, unknown>
                        return {
                          id: typeof rr.id === 'string' ? rr.id : `branch_${Date.now()}`,
                          condition: typeof rr.condition === 'string' ? rr.condition : '',
                          outcome: typeof rr.outcome === 'string' ? (rr.outcome as any) : '',
                          steps: [],
                        }
                      })
                      .filter(Boolean) as RiskBranch[])
                  : []

              return { id, type, branches }
            }

            return null
          })
          .filter(Boolean) as WorkflowStep[]
      }

      const normalizeWorkflowsConfig = (workflows: unknown): Workflow[] => {
        if (!Array.isArray(workflows)) return []
        return workflows
          .map((w): Workflow | null => {
            if (!w || typeof w !== 'object') return null
            const wr = w as Record<string, unknown>
            if (typeof wr.id !== 'string') return null

            const kind = wr.kind === 'complex' ? 'complex' : 'builder'

            if (kind === 'complex') {
              return {
                kind: 'complex',
                id: wr.id,
                name: typeof wr.name === 'string' ? wr.name : '',
                workflowId: typeof wr.workflowId === 'string' ? wr.workflowId : '',
                url: typeof wr.url === 'string' ? wr.url : '',
                notes: typeof wr.notes === 'string' ? wr.notes : '',
              }
            }

            return {
              kind: 'builder',
              id: wr.id,
              name: typeof wr.name === 'string' ? wr.name : '',
              notes: typeof wr.notes === 'string' ? wr.notes : '',
              associatedFlowId: typeof wr.associatedFlowId === 'string' ? wr.associatedFlowId : null,
              steps: normalizeWorkflowSteps(wr.steps),
              ...(typeof wr.v1RecipeKey === 'string' ? { v1RecipeKey: wr.v1RecipeKey } : {}),
              ...(Array.isArray(wr.v1CustomChecks)
                ? {
                    v1CustomChecks: wr.v1CustomChecks.map((c: any) => ({
                      id: typeof c.id === 'string' ? c.id : `check_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                      checkTypeId: typeof c.checkTypeId === 'string' ? c.checkTypeId : '',
                      stopOnFail: c.stopOnFail === true,
                      optional: c.optional === true,
                    })),
                  }
                : {}),
            }
          })
          .filter(Boolean) as Workflow[]
      }

      const normalized: ScopeData = {
        ...getDefaultScopeData(),
        ...scope,
        generalInfo: {
          ...getDefaultGeneralInfo(),
          ...(scope.generalInfo || {}),
          customWorks: ((scope.generalInfo || {} as any).customWorks || []).map((item: any) => ({
            ...item,
            jiraTickets: item.jiraTickets || [],
            pmApprovalStatus: item.pmApprovalStatus || 'not_requested',
            tasksLinked: item.tasksLinked || false,
          })),
        },
        kycConfig: normalizeKycConfig(scope.kycConfig),
        amlConfig: normalizeAmlConfig(scope.amlConfig),
        biometricsConfig: normalizeBiometricsConfig(scope.biometricsConfig),
        kybConfig: normalizeKybConfig(scope.kybConfig),
        rulesConfig: { ...getDefaultRulesConfig(), ...(scope.rulesConfig || {}) },
        riskConfig: normalizeRiskConfig(scope.riskConfig),
        recipesConfig: scope.recipesConfig || [],
        workflowsConfig: normalizeWorkflowsConfig(scope.workflowsConfig),
        scopeHours: scope.scopeHours || [],
        featureRequests: scope.featureRequests || [],
      }

      return {
        scope: normalized,
        tabCompletions: calculateTabCompletion(normalized),
      }
    }),

  resetScope: () => {
    const defaultScope = getDefaultScopeData()
    set({
      scope: defaultScope,
      tabCompletions: calculateTabCompletion(defaultScope),
    })
  },

  updateCustomerName: (name) =>
    set((state) => {
      const newScope = { ...state.scope, customerName: name }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateCustomerAbn: (abn) =>
    set((state) => {
      const newScope = { ...state.scope, customerAbn: abn }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateHubspotDeal: (dealId, dealName) =>
    set((state) => {
      const newScope = { ...state.scope, hubspotDealId: dealId, hubspotDealName: dealName }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // General Info
  updateGeneralInfo: (field, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        generalInfo: { ...state.scope.generalInfo, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // KYC Config
  updateKycConfig: (field, value) =>
    set((state) => {
      // When KYC is turned off, reset all KYC config to defaults so stale
      // data (e.g. from a cloned deal) doesn't persist invisibly.
      if (field === 'kycRequired' && value === 'no') {
        const fresh = getDefaultKycConfig()
        fresh.kycRequired = 'no'
        const newScope = { ...state.scope, kycConfig: fresh }
        return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
      }
      const newScope = {
        ...state.scope,
        kycConfig: { ...state.scope.kycConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleKycDocumentAu: (docId) =>
    set((state) => {
      const current = state.scope.kycConfig.documentTypesAu
      const updated = current.includes(docId) ? current.filter((d) => d !== docId) : [...current, docId]
      const newScope = {
        ...state.scope,
        kycConfig: { ...state.scope.kycConfig, documentTypesAu: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleKycDocumentNz: (docId) =>
    set((state) => {
      const current = state.scope.kycConfig.documentTypesNz
      const updated = current.includes(docId) ? current.filter((d) => d !== docId) : [...current, docId]
      const newScope = {
        ...state.scope,
        kycConfig: { ...state.scope.kycConfig, documentTypesNz: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addKycInternationalCountry: (country) =>
    set((state) => {
      if (state.scope.kycConfig.internationalCountries.includes(country)) return state
      const newScope = {
        ...state.scope,
        kycConfig: {
          ...state.scope.kycConfig,
          internationalCountries: [...state.scope.kycConfig.internationalCountries, country],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeKycInternationalCountry: (country) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kycConfig: {
          ...state.scope.kycConfig,
          internationalCountries: state.scope.kycConfig.internationalCountries.filter((c) => c !== country),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // KYC Ruleset actions
  addKycRuleset: (rulesetKey, name) =>
    set((state) => {
      const maxId = Math.max(0, ...state.scope.kycConfig.rulesets.map((r) => r.id))
      const newRuleset = {
        id: maxId + 1,
        rulesetKey,
        name: name || undefined,
        customRequirements: rulesetKey === 'custom' ? {
          dataSources: 0,
          address: 0,
          dateOfBirth: 0,
          fullName: 0,
          govId: 0,
        } : undefined,
      }
      const newScope = {
        ...state.scope,
        kycConfig: {
          ...state.scope.kycConfig,
          rulesets: [...state.scope.kycConfig.rulesets, newRuleset],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateKycRuleset: (id, rulesetKey, customRequirements, name) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kycConfig: {
          ...state.scope.kycConfig,
          rulesets: state.scope.kycConfig.rulesets.map((r) =>
            r.id === id
              ? {
                  ...r,
                  rulesetKey,
                  customRequirements,
                  name: name || r.name,
                }
              : r
          ),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeKycRuleset: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kycConfig: {
          ...state.scope.kycConfig,
          rulesets: state.scope.kycConfig.rulesets.filter((r) => r.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // AML Config
  updateAmlConfig: (field, value) =>
    set((state) => {
      // When AML is turned off (N/A), reset all AML config to defaults.
      if (field === 'level' && (value === 'na' || value === '')) {
        const fresh = getDefaultAmlConfig()
        fresh.level = value as AmlConfig['level']
        const newScope = { ...state.scope, amlConfig: fresh }
        return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
      }
      const newScope = {
        ...state.scope,
        amlConfig: { ...state.scope.amlConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  togglePepLevel: (level) =>
    set((state) => {
      const current = state.scope.amlConfig.pepLevels
      const updated = current.includes(level) ? current.filter((l) => l !== level) : [...current, level]
      const newScope = {
        ...state.scope,
        amlConfig: { ...state.scope.amlConfig, pepLevels: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Biometrics Config
  updateBiometricsConfig: (field, value) =>
    set((state) => {
      // When Biometrics is turned off, reset all config to defaults.
      if (field === 'enabled' && value === 'no') {
        const fresh = getDefaultBiometricsConfig()
        fresh.enabled = 'no'
        const newScope = { ...state.scope, biometricsConfig: fresh }
        return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
      }
      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleBiometricsDocumentType: (docId) =>
    set((state) => {
      const current = state.scope.biometricsConfig.documentTypes
      if (docId === 'unknown') {
        const updated = current.includes('unknown') ? current.filter((d) => d !== 'unknown') : ['unknown']
        const newScope = {
          ...state.scope,
          biometricsConfig: { ...state.scope.biometricsConfig, documentTypes: updated },
        }
        return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
      }

      const withoutUnknown = current.filter((d) => d !== 'unknown')
      const updated = withoutUnknown.includes(docId)
        ? withoutUnknown.filter((d) => d !== docId)
        : [...withoutUnknown, docId]
      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, documentTypes: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBiometricsFlow: () =>
    set((state) => {
      const flows = state.scope.biometricsConfig.flows
      if (flows.length >= 5) return state

      const nextNumber = flows.length + 1
      const newFlow: BiometricsConfig['flows'][number] = {
        id: `flow_${nextNumber}`,
        name: `Flow ${nextNumber}`,
        notes: '',
        isDefault: flows.length === 0,
        idVerificationEnabled: 'unknown',
        idFormEnabled: 'unknown',
        biometricsEnabled: 'unknown',
        reviewScreenEnabled: 'unknown',
        consentScreenEnabled: 'unknown',
        redirectPageEnabled: 'unknown',
        customScreenEnabled: 'unknown',
        customScreenDetails: '',
        doubleIdEnabled: 'unknown',
        vendor: 'unknown',
        documentTypes: ['unknown'],
        customDocumentTypeDetails: '',
      }

      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, flows: [...flows, newFlow] },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  applyBiometricsFlowTemplate: (index, templateId) =>
    set((state) => {
      const bio = state.scope.biometricsConfig
      const flows = bio.flows
      if (index < 0 || index >= flows.length) return state

      const isDaon = templateId === 'idv_daon'
      const templateName = isDaon ? 'IDV Daon' : 'IDV Incode'
      const vendor: BiometricsConfig['flows'][number]['vendor'] = isDaon ? 'daon' : 'incode'

      const current = flows[index]
      const keepsCustomName = current.name.trim() !== '' && !/^Flow \d+$/.test(current.name.trim())

      const updatedFlow: BiometricsConfig['flows'][number] = {
        ...current,
        id: templateId,
        name: keepsCustomName ? current.name : templateName,
        consentScreenEnabled: 'yes',
        idFormEnabled: 'no',
        idVerificationEnabled: 'yes',
        doubleIdEnabled: 'no',
        biometricsEnabled: 'yes',
        reviewScreenEnabled: 'no',
        redirectPageEnabled: 'no',
        vendor,
        documentTypes: ['passport', 'drivers_license'],
      }

      const updatedFlows = flows.map((f, i) => (i === index ? updatedFlow : f))
      const daonAutoNote = "IDV Daon: ID verification of Passport and Driver's Licence, with Biometrics."
      const shouldPopulateDaonNotes = isDaon && bio.daonEngagementNotes.trim() === ''

      const newScope = {
        ...state.scope,
        biometricsConfig: {
          ...bio,
          flows: updatedFlows,
          ...(shouldPopulateDaonNotes ? { daonEngagementNotes: daonAutoNote } : {}),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateBiometricsFlow: (index, updates) =>
    set((state) => {
      const flows = state.scope.biometricsConfig.flows
      if (index < 0 || index >= flows.length) return state
      const updatedFlows = flows.map((f, i) => (i === index ? { ...f, ...updates } : f))
      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, flows: updatedFlows },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeBiometricsFlow: (index) =>
    set((state) => {
      const flows = state.scope.biometricsConfig.flows
      if (index < 0 || index >= flows.length) return state
      const updatedFlows = flows.filter((_, i) => i !== index)
      const hasDefault = updatedFlows.some((f) => f.isDefault)
      const normalized = hasDefault
        ? updatedFlows
        : updatedFlows.map((f, i) => ({ ...f, isDefault: i === 0 }))
      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, flows: normalized },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  setDefaultBiometricsFlow: (index) =>
    set((state) => {
      const flows = state.scope.biometricsConfig.flows
      if (index < 0 || index >= flows.length) return state
      const updatedFlows = flows.map((f, i) => ({ ...f, isDefault: i === index }))
      const newScope = {
        ...state.scope,
        biometricsConfig: { ...state.scope.biometricsConfig, flows: updatedFlows },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // KYB Config
  updateKybConfig: (field, value) =>
    set((state) => {
      // When KYB is turned off, reset all KYB config to defaults.
      if (field === 'enabled' && value === 'no') {
        const fresh = getDefaultKybConfig()
        fresh.enabled = 'no'
        const newScope = { ...state.scope, kybConfig: fresh }
        return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
      }
      const newScope = {
        ...state.scope,
        kybConfig: { ...state.scope.kybConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleKybAuCheck: (checkId) =>
    set((state) => {
      const current = state.scope.kybConfig.auChecks
      const toggled = !current[checkId]
      // AML Standard and AML Enhanced are mutually exclusive
      const auChecks = { ...current, [checkId]: toggled }
      if (toggled && checkId === 'amlStandard') auChecks.amlEnhanced = false
      if (toggled && checkId === 'amlEnhanced') auChecks.amlStandard = false
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          auChecks,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleAmlEntityScreeningType: (type) =>
    set((state) => {
      const current = state.scope.kybConfig.amlEntityScreeningTypes
      const updated = current.includes(type) ? current.filter((t) => t !== type) : [...current, type]
      const newScope = {
        ...state.scope,
        kybConfig: { ...state.scope.kybConfig, amlEntityScreeningTypes: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateEntityTypeSplit: (field, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          entityTypeSplit: { ...state.scope.kybConfig.entityTypeSplit, [field]: value },
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addKybInternationalCountry: (country) =>
    set((state) => {
      if (state.scope.kybConfig.internationalCountryProducts.some((c) => c.country === country)) return state
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          internationalCountryProducts: [...state.scope.kybConfig.internationalCountryProducts, { country, products: [] }],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeKybInternationalCountry: (country) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          internationalCountryProducts: state.scope.kybConfig.internationalCountryProducts.filter((c) => c.country !== country),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleKybInternationalCountryProduct: (country, product) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          internationalCountryProducts: state.scope.kybConfig.internationalCountryProducts.map((cp) => {
            if (cp.country !== country) return cp
            const products = cp.products.includes(product) ? cp.products.filter((p) => p !== product) : [...cp.products, product]
            return { ...cp, products }
          }),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateTrustTypeSplit: (field, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        kybConfig: {
          ...state.scope.kybConfig,
          trustTypeSplit: { ...state.scope.kybConfig.trustTypeSplit, [field]: value },
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Recipes
  addRecipe: () =>
    set((state) => {
      const newRecipe: Recipe = {
        ...getDefaultRecipe(),
        id: Date.now(),
        name: `Recipe ${state.scope.recipesConfig.length + 1}`,
      }
      const newScope = {
        ...state.scope,
        recipesConfig: [...state.scope.recipesConfig, newRecipe],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateRecipe: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        recipesConfig: state.scope.recipesConfig.map((r) => (r.id === id ? { ...r, ...updates } : r)),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeRecipe: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        recipesConfig: state.scope.recipesConfig.filter((r) => r.id !== id),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleRecipeProduct: (recipeId, productId) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        recipesConfig: state.scope.recipesConfig.map((r) =>
          r.id === recipeId ? { ...r, products: { ...r.products, [productId]: !r.products[productId] } } : r
        ),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addWorkflow: () =>
    set((state) => {
      const newWorkflow: Workflow = {
        kind: 'builder',
        id: `workflow_${Date.now()}`,
        name: `Workflow ${state.scope.workflowsConfig.length + 1}`,
        notes: '',
        associatedFlowId: null,
        steps: [],
      }
      const newScope = {
        ...state.scope,
        workflowsConfig: [...state.scope.workflowsConfig, newWorkflow],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addPresetWorkflow: (preset) =>
    set((state) => {
      const stepId = () => `step_${Date.now()}_${Math.random().toString(16).slice(2)}`
      const branchId = () => `branch_${Date.now()}_${Math.random().toString(16).slice(2)}`

      // Determine which ruleset key to use
      const rulesetKey = preset === 'simple' ? '1x1_gov_id' : '2x2_gov_id'

      // Check if ruleset already exists in KYC config
      const rulesetExists = state.scope.kycConfig.rulesets.some((r) => r.rulesetKey === rulesetKey)

      // Auto-add ruleset to KYC tab if it doesn't exist
      let updatedKycConfig = state.scope.kycConfig
      if (!rulesetExists) {
        const nextId = Math.max(0, ...state.scope.kycConfig.rulesets.map((r) => r.id)) + 1
        updatedKycConfig = {
          ...state.scope.kycConfig,
          rulesets: [
            ...state.scope.kycConfig.rulesets,
            {
              id: nextId,
              rulesetKey,
              name: undefined,
              customRequirements: undefined,
            },
          ],
        }
      }

      const makeKyc = (rulesetId: string): WorkflowStep => ({
        id: stepId(),
        type: 'kyc',
        rulesetId,
        cascadeSources: [],
      })

      const makeRiskStepUp = (): WorkflowStep => ({
        id: stepId(),
        type: 'risk',
        branches: [
          {
            id: branchId(),
            condition: 'Step-up to IDV/Biometrics',
            outcome: '' as RiskBranchOutcome,
            steps: [{ id: stepId(), type: 'idv_biometrics', flowId: null, requirement: 'mandatory' }],
          },
        ],
      })

      const makeWorkflow = (): Workflow => {
        const id = `workflow_${Date.now()}_${Math.random().toString(16).slice(2)}`

        if (preset === 'full') {
          return {
            kind: 'builder',
            id,
            name: `Workflow ${state.scope.workflowsConfig.length + 1}`,
            notes: '',
            associatedFlowId: null,
            steps: [
              { id: stepId(), type: 'blocklist' },
              { id: stepId(), type: 'gov_id' },
              makeKyc('2x2_gov_id'),
              { id: stepId(), type: 'idv_biometrics', flowId: null, requirement: 'mandatory' },
              { id: stepId(), type: 'aml' },
            ],
          }
        }

        if (preset === 'step_up') {
          return {
            kind: 'builder',
            id,
            name: `Workflow ${state.scope.workflowsConfig.length + 1}`,
            notes: '',
            associatedFlowId: null,
            steps: [{ id: stepId(), type: 'blocklist' }, { id: stepId(), type: 'gov_id' }, makeKyc('2x2_gov_id'), makeRiskStepUp(), { id: stepId(), type: 'aml' }],
          }
        }

        return {
          kind: 'builder',
          id,
          name: `Workflow ${state.scope.workflowsConfig.length + 1}`,
          notes: '',
          associatedFlowId: null,
          steps: [makeKyc('1x1_gov_id')],
        }
      }

      const newWorkflow = makeWorkflow()
      const newScope = {
        ...state.scope,
        kycConfig: updatedKycConfig,
        workflowsConfig: [...state.scope.workflowsConfig, newWorkflow],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addV1RecipeWorkflow: (recipeKey) =>
    set((state) => {
      const recipe = V1_RECIPES.find((r) => r.key === recipeKey)
      if (!recipe) return state

      const id = `workflow_${Date.now()}_${Math.random().toString(16).slice(2)}`

      const newWorkflow: BuilderWorkflow = {
        kind: 'builder',
        id,
        name: recipe.label,
        notes: recipe.description,
        associatedFlowId: null,
        steps: [],
        v1RecipeKey: recipe.key,
      }

      const newScope = {
        ...state.scope,
        workflowsConfig: [...state.scope.workflowsConfig, newWorkflow],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addV1CustomWorkflow: () =>
    set((state) => {
      const id = `workflow_${Date.now()}_${Math.random().toString(16).slice(2)}`
      const newWorkflow: BuilderWorkflow = {
        kind: 'builder',
        id,
        name: `Custom Profile ${state.scope.workflowsConfig.length + 1}`,
        notes: '',
        associatedFlowId: null,
        steps: [],
        v1CustomChecks: [],
      }
      const newScope = {
        ...state.scope,
        workflowsConfig: [...state.scope.workflowsConfig, newWorkflow],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addComplexWorkflow: () =>
    set((state) => {
      const newWorkflow: Workflow = {
        kind: 'complex',
        id: `workflow_${Date.now()}`,
        name: `Complex workflow ${state.scope.workflowsConfig.length + 1}`,
        workflowId: '',
        url: '',
        notes: '',
      }
      const newScope = {
        ...state.scope,
        workflowsConfig: [...state.scope.workflowsConfig, newWorkflow],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateWorkflow: (id, updates) =>
    set((state) => {
      const builderUpdates = updates as Partial<Extract<Workflow, { kind?: 'builder' }>>
      const complexUpdates = updates as Partial<Extract<Workflow, { kind: 'complex' }>>

      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) => {
          if (w.id !== id) return w

          if (w.kind === 'complex') {
            return {
              ...w,
              name: typeof complexUpdates.name === 'string' ? complexUpdates.name : w.name,
              workflowId: typeof complexUpdates.workflowId === 'string' ? complexUpdates.workflowId : w.workflowId,
              url: typeof complexUpdates.url === 'string' ? complexUpdates.url : w.url,
              notes: typeof complexUpdates.notes === 'string' ? complexUpdates.notes : w.notes,
            }
          }

          return {
            ...w,
            name: typeof builderUpdates.name === 'string' ? builderUpdates.name : w.name,
            notes: typeof builderUpdates.notes === 'string' ? builderUpdates.notes : w.notes,
            associatedFlowId: 'associatedFlowId' in builderUpdates ? (builderUpdates.associatedFlowId as any) : w.associatedFlowId,
            steps: Array.isArray((builderUpdates as any).steps) ? (builderUpdates as any).steps : w.steps,
            v1CustomChecks: Array.isArray((builderUpdates as any).v1CustomChecks) ? (builderUpdates as any).v1CustomChecks : (w as any).v1CustomChecks,
          }
        }),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeWorkflow: (id) =>
    set((state) => {
      const newScope = { ...state.scope, workflowsConfig: state.scope.workflowsConfig.filter((w) => w.id !== id) }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addWorkflowStep: (workflowId, stepType) =>
    set((state) => {
      const newStepId = `step_${Date.now()}`

      const base = { id: newStepId } as const
      const newStep: WorkflowStep =
        stepType === 'blocklist'
          ? { ...base, type: 'blocklist' }
          : stepType === 'gov_id'
            ? { ...base, type: 'gov_id' }
            : stepType === 'kyc'
              ? { ...base, type: 'kyc', rulesetId: '', cascadeSources: [] }
              : stepType === 'idv_biometrics'
                ? { ...base, type: 'idv_biometrics', flowId: null, requirement: 'mandatory' }
                : stepType === 'aml'
                  ? { ...base, type: 'aml' }
                  : stepType === 'fraud'
                    ? { ...base, type: 'fraud' }
                    : stepType === 'death_check'
                      ? { ...base, type: 'death_check' }
                      : stepType === 'duplicate'
                        ? { ...base, type: 'duplicate', notes: '' }
                        : { ...base, type: 'risk', branches: [] }

      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) => {
          if (w.id !== workflowId) return w

          if (w.kind === 'complex') return w

          const nextSteps = [...w.steps, newStep]

          if (stepType === 'gov_id') {
            return {
              ...w,
              steps: nextSteps.map((s) =>
                s.type === 'kyc' && typeof s.rulesetId === 'string' && s.rulesetId.includes('no_gov_id')
                  ? { ...s, rulesetId: '' }
                  : s
              ),
            }
          }

          return { ...w, steps: nextSteps }
        }),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateWorkflowStep: (workflowId, stepId, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) => {
          if (w.id !== workflowId) return w
          if (w.kind === 'complex') return w
          return { ...w, steps: w.steps.map((s) => (s.id === stepId ? ({ ...s, ...updates } as WorkflowStep) : s)) }
        }),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeWorkflowStep: (workflowId, stepId) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) =>
          w.id === workflowId ? (w.kind === 'complex' ? w : { ...w, steps: w.steps.filter((s) => s.id !== stepId) }) : w
        ),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  moveWorkflowStep: (workflowId, stepId, direction) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) => {
          if (w.id !== workflowId) return w
          if (w.kind === 'complex') return w
          const idx = w.steps.findIndex((s) => s.id === stepId)
          if (idx < 0) return w
          const nextIdx = direction === 'up' ? idx - 1 : idx + 1
          if (nextIdx < 0 || nextIdx >= w.steps.length) return w
          const newSteps = w.steps.slice()
          const tmp = newSteps[idx]
          newSteps[idx] = newSteps[nextIdx]
          newSteps[nextIdx] = tmp
          return { ...w, steps: newSteps }
        }),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  reorderWorkflowStep: (workflowId, stepId, targetIndex) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        workflowsConfig: state.scope.workflowsConfig.map((w) => {
          if (w.id !== workflowId) return w
          if (w.kind === 'complex') return w
          const fromIndex = w.steps.findIndex((s) => s.id === stepId)
          if (fromIndex < 0) return w

          const clampedTarget = Math.max(0, Math.min(targetIndex, w.steps.length - 1))
          if (clampedTarget === fromIndex) return w

          const next = w.steps.slice()
          const [moved] = next.splice(fromIndex, 1)
          next.splice(clampedTarget, 0, moved)
          return { ...w, steps: next }
        }),
      }

      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateWorkflowsMeta: (field, value) =>
    set((state) => {
      const newScope = { ...state.scope, [field]: value }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Rules Config
  updateRulesConfig: (field, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        rulesConfig: { ...state.scope.rulesConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addCustomDuplicateRule: () =>
    set((state) => {
      const rules = state.scope.rulesConfig.customDuplicateRuleSet || []
      const newRule = {
        id: `dup_rule_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: '',
        matchFields: [] as import('./types').DuplicateMatchField[],
        method: 'EXACT' as const,
        strength: 'MEDIUM' as const,
      }
      const newScope = {
        ...state.scope,
        rulesConfig: {
          ...state.scope.rulesConfig,
          customDuplicateRuleSet: [...rules, newRule],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateCustomDuplicateRule: (index, updates) =>
    set((state) => {
      const rules = state.scope.rulesConfig.customDuplicateRuleSet || []
      if (index < 0 || index >= rules.length) return state
      const updatedRules = rules.map((r, i) => (i === index ? { ...r, ...updates } : r))
      const newScope = {
        ...state.scope,
        rulesConfig: { ...state.scope.rulesConfig, customDuplicateRuleSet: updatedRules },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeCustomDuplicateRule: (index) =>
    set((state) => {
      const rules = state.scope.rulesConfig.customDuplicateRuleSet || []
      if (index < 0 || index >= rules.length) return state
      const newScope = {
        ...state.scope,
        rulesConfig: {
          ...state.scope.rulesConfig,
          customDuplicateRuleSet: rules.filter((_, i) => i !== index),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addCustomBlocklistEntry: () =>
    set((state) => {
      const entries = state.scope.rulesConfig.customBlocklistEntries || []
      const newEntry = {
        id: `bl_entry_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: '',
        dataType: 'EMAIL' as const,
        action: 'BLOCK' as const,
        riskScore: null as number | null,
        description: '',
      }
      const newScope = {
        ...state.scope,
        rulesConfig: {
          ...state.scope.rulesConfig,
          customBlocklistEntries: [...entries, newEntry],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateCustomBlocklistEntry: (index, updates) =>
    set((state) => {
      const entries = state.scope.rulesConfig.customBlocklistEntries || []
      if (index < 0 || index >= entries.length) return state
      const updated = entries.map((e, i) => (i === index ? { ...e, ...updates } : e))
      const newScope = {
        ...state.scope,
        rulesConfig: { ...state.scope.rulesConfig, customBlocklistEntries: updated },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeCustomBlocklistEntry: (index) =>
    set((state) => {
      const entries = state.scope.rulesConfig.customBlocklistEntries || []
      if (index < 0 || index >= entries.length) return state
      const newScope = {
        ...state.scope,
        rulesConfig: {
          ...state.scope.rulesConfig,
          customBlocklistEntries: entries.filter((_, i) => i !== index),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Risk Config
  setRiskConfig: (riskConfig) =>
    set((state) => {
      const newScope = { ...state.scope, riskConfig }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateRiskConfig: (field, value) =>
    set((state) => {
      const newScope = { ...state.scope, riskConfig: { ...state.scope.riskConfig, [field]: value } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Scope Hours
  addScopeHour: (hour) =>
    set((state) => {
      const newHour: ScopeHour = { ...hour, id: Date.now() }
      const newScope = {
        ...state.scope,
        scopeHours: [...state.scope.scopeHours, newHour],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateScopeHour: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        scopeHours: state.scope.scopeHours.map((h) => (h.id === id ? { ...h, ...updates } : h)),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeScopeHour: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        scopeHours: state.scope.scopeHours.filter((h) => h.id !== id),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Feature Requests
  addFeatureRequest: () =>
    set((state) => {
      const newRequest: FeatureRequest = {
        id: Date.now(),
        name: '',
        description: '',
        hours: 0,
      }
      const newScope = {
        ...state.scope,
        featureRequests: [...state.scope.featureRequests, newRequest],
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateFeatureRequest: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        featureRequests: state.scope.featureRequests.map((r) => (r.id === id ? { ...r, ...updates } : r)),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeFeatureRequest: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        featureRequests: state.scope.featureRequests.filter((r) => r.id !== id),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // SOW Config
  updateSowConfig: (field, value) =>
    set((state) => {
      const section = getSectionForField(field)
      const newScope = {
        ...state.scope,
        sowConfig: { ...state.scope.sowConfig, [field]: value },
        ...(section ? autoMarkSowSection(state, section) : {}),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addSowMilestone: () =>
    set((state) => {
      const newMilestone: SowMilestone = {
        id: Date.now(),
        name: '',
        date: '',
        description: '',
      }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          milestones: [...state.scope.sowConfig.milestones, newMilestone],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateSowMilestone: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          milestones: state.scope.sowConfig.milestones.map((m) => (m.id === id ? { ...m, ...updates } : m)),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeSowMilestone: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          milestones: state.scope.sowConfig.milestones.filter((m) => m.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addSowPhase: () =>
    set((state) => {
      const newPhase: SowPhase = {
        id: Date.now(),
        name: '',
        description: '',
        duration: '',
        startDate: '',
        endDate: '',
        deliverables: [],
      }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          phases: [...state.scope.sowConfig.phases, newPhase],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateSowPhase: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          phases: state.scope.sowConfig.phases.map((p) => (p.id === id ? { ...p, ...updates } : p)),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeSowPhase: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          phases: state.scope.sowConfig.phases.filter((p) => p.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Upsert an SOW phase slot at the given index (aligned with generalInfo.phases)
  // and update its start/end dates. Fills any missing earlier slots with blanks.
  setSowPhaseDatesByIndex: (index, patch) =>
    set((state) => {
      const existing = state.scope.sowConfig.phases
      const next = existing.slice()
      while (next.length <= index) {
        next.push({
          id: Date.now() + next.length,
          name: '',
          description: '',
          duration: '',
          startDate: '',
          endDate: '',
          deliverables: [],
        })
      }
      next[index] = { ...next[index], ...patch }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          phases: next,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addSowActivity: (phaseId) =>
    set((state) => {
      const phase = state.scope.sowConfig.phases.find((p) => p.id === phaseId)
      const newActivity: SowActivity = {
        id: Date.now(),
        phaseId,
        phaseName: phase?.name || '',
        activityName: '',
        description: '',
        outcome: '',
        deliverables: [],
        deliveryDate: '',
      }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          activities: [...state.scope.sowConfig.activities, newActivity],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateSowActivity: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          activities: state.scope.sowConfig.activities.map((a) => (a.id === id ? { ...a, ...updates } : a)),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeSowActivity: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'phases'),
        sowConfig: {
          ...state.scope.sowConfig,
          activities: state.scope.sowConfig.activities.filter((a) => a.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addGanttTask: (phaseId) =>
    set((state) => {
      const newTask: GanttTask = {
        id: Date.now(),
        phaseId,
        taskName: '',
        startDate: '',
        endDate: '',
        dependencies: [],
        resources: [],
        responsiblePerson: '',
        party: '',
      }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'gantt'),
        sowConfig: {
          ...state.scope.sowConfig,
          ganttTasks: [...state.scope.sowConfig.ganttTasks, newTask],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateGanttTask: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'gantt'),
        sowConfig: {
          ...state.scope.sowConfig,
          ganttTasks: state.scope.sowConfig.ganttTasks.map((t) => (t.id === id ? { ...t, ...updates } : t)),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeGanttTask: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'gantt'),
        sowConfig: {
          ...state.scope.sowConfig,
          ganttTasks: state.scope.sowConfig.ganttTasks.filter((t) => t.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  reorderGanttTask: (id, direction) =>
    set((state) => {
      const tasks = [...state.scope.sowConfig.ganttTasks]
      const idx = tasks.findIndex((t) => t.id === id)
      if (idx < 0) return state
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (swapIdx < 0 || swapIdx >= tasks.length) return state
      ;[tasks[idx], tasks[swapIdx]] = [tasks[swapIdx], tasks[idx]]
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'gantt'),
        sowConfig: { ...state.scope.sowConfig, ganttTasks: tasks },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // RACI Chart
  addRaciColumn: (name) =>
    set((state) => {
      const newCol: RaciColumn = { id: `col_${Date.now()}`, name }
      const updatedEntries = state.scope.sowConfig.raciEntries.map((entry) => ({
        ...entry,
        assignments: { ...entry.assignments, [newCol.id]: '' as RaciValue },
      }))
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciColumns: [...state.scope.sowConfig.raciColumns, newCol],
          raciEntries: updatedEntries,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeRaciColumn: (columnId) =>
    set((state) => {
      const updatedEntries = state.scope.sowConfig.raciEntries.map((entry) => {
        const { [columnId]: _, ...rest } = entry.assignments
        return { ...entry, assignments: rest }
      })
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciColumns: state.scope.sowConfig.raciColumns.filter((c) => c.id !== columnId),
          raciEntries: updatedEntries,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateRaciColumnName: (columnId, name) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciColumns: state.scope.sowConfig.raciColumns.map((c) =>
            c.id === columnId ? { ...c, name } : c
          ),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addRaciEntry: () =>
    set((state) => {
      const emptyAssignments: Record<string, RaciValue> = {}
      state.scope.sowConfig.raciColumns.forEach((col) => {
        emptyAssignments[col.id] = ''
      })
      const newEntry: RaciEntry = { id: Date.now(), task: '', assignments: emptyAssignments }
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciEntries: [...state.scope.sowConfig.raciEntries, newEntry],
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateRaciEntry: (id, updates) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciEntries: state.scope.sowConfig.raciEntries.map((e) =>
            e.id === id ? { ...e, ...updates } : e
          ),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateRaciAssignment: (entryId, columnId, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciEntries: state.scope.sowConfig.raciEntries.map((e) =>
            e.id === entryId
              ? { ...e, assignments: { ...e.assignments, [columnId]: value } }
              : e
          ),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  removeRaciEntry: (id) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciEntries: state.scope.sowConfig.raciEntries.filter((e) => e.id !== id),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  loadDefaultRaci: () =>
    set((state) => {
      const { columns, entries } = getDefaultRaciTemplate()
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'raci'),
        sowConfig: {
          ...state.scope.sowConfig,
          raciColumns: columns,
          raciEntries: entries,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  loadDefaultGantt: () =>
    set((state) => {
      const tasks = getDefaultGanttTemplate()
      const newScope = {
        ...state.scope,
        ...autoMarkSowSection(state, 'gantt'),
        sowConfig: {
          ...state.scope.sowConfig,
          ganttTasks: tasks,
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // SOW Template
  setSowTemplate: (template) => set({ sowTemplate: template }),

  resetSowSection: (sectionKey) =>
    set((state) => {
      if (!state.sowTemplate) return state
      const fieldsToReset = SOW_SECTION_FIELDS[sectionKey]
      const resetSow = { ...state.scope.sowConfig }
      for (const field of fieldsToReset) {
        ;(resetSow as any)[field] = (state.sowTemplate as any)[field]
      }
      const newScope = {
        ...state.scope,
        sowConfig: resetSow,
        sowCustomizedSections: unmarkSectionCustomized(state.scope.sowCustomizedSections, sectionKey),
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Finalization
  finalizeScope: (finalizedBy) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        finalized: true,
        finalizedAt: new Date().toISOString(),
        finalizedBy,
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  unfinalizeScope: () =>
    set((state) => {
      const newScope = {
        ...state.scope,
        finalized: false,
        finalizedAt: null,
        finalizedBy: null,
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Blueprint
  updateBlueprintField: (field, value) =>
    set((state) => {
      const newScope = {
        ...state.scope,
        blueprintConfig: { ...state.scope.blueprintConfig, [field]: value },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBlueprintVendor: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newVendor: BlueprintCurrentVendor = { id: Date.now(), function: 'kyc', vendorName: '', notes: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, currentVendors: [...bp.currentVendors, newVendor] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintVendor: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, currentVendors: bp.currentVendors.map((v) => (v.id === id ? { ...v, ...updates } : v)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintVendor: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, currentVendors: bp.currentVendors.filter((v) => v.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBlueprintSignal: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newSignal: BlueprintSignalEntry = { id: Date.now(), signalType: '', geo: '', status: 'unknown', notes: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, signals: [...bp.signals, newSignal] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintSignal: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, signals: bp.signals.map((s) => (s.id === id ? { ...s, ...updates } : s)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintSignal: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, signals: bp.signals.filter((s) => s.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBlueprintCountryRuleset: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newCr: BlueprintCountryRuleset = { id: Date.now(), country: '', dataInputs: getDefaultDataInputs(), ruleset: 'unknown', govIdRequired: false, nationalIds: [], nationalIdNotes: '', docScanVendors: [], selfieVendors: [], deviceDataVendors: [], passRate: '', passRateConfirmed: false, desiredPassRate: '', desiredPassRateConfirmed: false, notes: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, countryRulesets: [...bp.countryRulesets, newCr] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintCountryRuleset: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, countryRulesets: bp.countryRulesets.map((c) => (c.id === id ? { ...c, ...updates } : c)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintCountryRuleset: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, countryRulesets: bp.countryRulesets.filter((c) => c.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  toggleBlueprintCountryInputData: (id, dataType) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = {
        ...state.scope,
        blueprintConfig: {
          ...bp,
          countryRulesets: bp.countryRulesets.map((c) => {
            if (c.id !== id) return c
            return { ...c, dataInputs: c.dataInputs.map((di: BlueprintDataInput) => di.type === dataType ? { ...di, enabled: !di.enabled } : di) }
          }),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintCountryInputFuzziness: (id, dataType, fuzziness) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = {
        ...state.scope,
        blueprintConfig: {
          ...bp,
          countryRulesets: bp.countryRulesets.map((c) => {
            if (c.id !== id) return c
            return { ...c, dataInputs: c.dataInputs.map((di: BlueprintDataInput) => di.type === dataType ? { ...di, fuzziness } : di) }
          }),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintCountryInputCaptureMethod: (id, dataType, captureMethod) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = {
        ...state.scope,
        blueprintConfig: {
          ...bp,
          countryRulesets: bp.countryRulesets.map((c) => {
            if (c.id !== id) return c
            return { ...c, dataInputs: c.dataInputs.map((di: BlueprintDataInput) => di.type === dataType ? { ...di, captureMethod } : di) }
          }),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintCountryInputField: (id, dataType, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = {
        ...state.scope,
        blueprintConfig: {
          ...bp,
          countryRulesets: bp.countryRulesets.map((c) => {
            if (c.id !== id) return c
            return { ...c, dataInputs: c.dataInputs.map((di: BlueprintDataInput) => di.type === dataType ? { ...di, ...updates } : di) }
          }),
        },
      }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBlueprintStakeholder: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newSh: BlueprintStakeholder = { id: Date.now(), name: '', role: '', technicalConcerns: '', engagementState: 'unknown' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, stakeholders: [...bp.stakeholders, newSh] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintStakeholder: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, stakeholders: bp.stakeholders.map((s) => (s.id === id ? { ...s, ...updates } : s)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintStakeholder: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, stakeholders: bp.stakeholders.filter((s) => s.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  addBlueprintPostCallUpdate: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newUpdate: BlueprintPostCallUpdate = { id: Date.now(), date: new Date().toISOString().split('T')[0], callWith: '', hypothesisChanges: '', gapsRemaining: '', nextCallProbes: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, postCallUpdates: [...bp.postCallUpdates, newUpdate] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintPostCallUpdate: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, postCallUpdates: bp.postCallUpdates.map((u) => (u.id === id ? { ...u, ...updates } : u)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintPostCallUpdate: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, postCallUpdates: bp.postCallUpdates.filter((u) => u.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  toggleBlueprintPrepCheck: (checkId) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newChecklist = bp.prepChecklist.map((c) => (c.id === checkId ? { ...c, checked: !c.checked } : c))
      const newScope = { ...state.scope, blueprintConfig: { ...bp, prepChecklist: newChecklist } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  resetBlueprintPrepChecklist: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, prepChecklist: DEFAULT_PREP_CHECKLIST.map((c) => ({ ...c })) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  updateBlueprintVerificationLayer: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, verificationStack: (bp.verificationStack || []).map((v) => (v.id === id ? { ...v, ...updates } : v)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  addBlueprintVerificationLayer: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newLayer: BlueprintVerificationLayer = { id: `vs_other_${Date.now()}`, layer: 'other', customLabel: '', vendor: '', owner: '', painPoints: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, verificationStack: [...(bp.verificationStack || []), newLayer] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintVerificationLayer: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, verificationStack: (bp.verificationStack || []).filter((v) => v.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintMaturityDimension: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, maturityDimensions: (bp.maturityDimensions || []).map((d) => (d.id === id ? { ...d, ...updates } : d)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  addBlueprintPriorityGap: () =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const gaps = bp.priorityGaps || []
      const newGap: BlueprintPriorityGap = { id: Date.now(), rank: gaps.length + 1, description: '', impact: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, priorityGaps: [...gaps, newGap] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintPriorityGap: (id, updates) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, priorityGaps: (bp.priorityGaps || []).map((g) => (g.id === id ? { ...g, ...updates } : g)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintPriorityGap: (id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const remaining = (bp.priorityGaps || []).filter((g) => g.id !== id).map((g, i) => ({ ...g, rank: i + 1 }))
      const newScope = { ...state.scope, blueprintConfig: { ...bp, priorityGaps: remaining } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  addBlueprintRecommendedAction: (bucket) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const existing = bp[bucket] || []
      const newAction: BlueprintRecommendedAction = { id: Date.now(), text: '' }
      const newScope = { ...state.scope, blueprintConfig: { ...bp, [bucket]: [...existing, newAction] } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  updateBlueprintRecommendedAction: (bucket, id, text) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, [bucket]: (bp[bucket] || []).map((a: BlueprintRecommendedAction) => (a.id === id ? { ...a, text } : a)) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),
  removeBlueprintRecommendedAction: (bucket, id) =>
    set((state) => {
      const bp = state.scope.blueprintConfig
      const newScope = { ...state.scope, blueprintConfig: { ...bp, [bucket]: (bp[bucket] || []).filter((a: BlueprintRecommendedAction) => a.id !== id) } }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  // Notes
  updateNotes: (notes) =>
    set((state) => {
      const newScope = { ...state.scope, notes }
      return { scope: newScope, tabCompletions: calculateTabCompletion(newScope) }
    }),

  setResellerConstraints: (constraints, parentName) =>
    set(() => ({ resellerConstraints: constraints, parentCustomerName: parentName })),
}))
